import React, { ReactNode, useState, useEffect, FC } from 'react';
import { cn } from '@/lib/utils';
import { useQueryNavigation } from '@/hooks';
import { FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';

type ItemType = {
  url?: string;
  element?: FC;
  label?: string;
};

type WrapperType = React.JSXElementConstructor<{
  item: ItemType;
  children: ReactNode;
  onActiveChange: (open: boolean) => void;
  className?: string;
}>;

interface NewSidebarProps {
  children: ReactNode;
  className?: string;
}

const Sidebar = ({ children, className }: NewSidebarProps) => {
  return (
    <aside
      className={cn(
        'text-white bg-accent rounded-r-2xl py-4 flex-col justify-between items-center gap-2 hidden xl:flex w-sidebar h-[calc(100vh-theme(spacing.header))]',
        className
      )}
    >
      {children}
    </aside>
  );
};

interface SidebarSectionProps {
  children: ReactNode;
  className?: string;
}

const SidebarSection = ({ children, className }: SidebarSectionProps) => {
  return <div className={cn('w-full flex justify-center items-center flex-col gap-2', className)}>{children}</div>;
};

interface SidebarItemProps {
  wrapper: WrapperType;
  item: ItemType;
  className?: string;
}

const SidebarItem = ({ wrapper, item }: SidebarItemProps) => {
  const [isActive, setIsActive] = useState(false);

  const Wrapper = wrapper;

  const Element = item.element;

  const handleActiveChange = (active: boolean) => {
    setIsActive(active);
  };

  return (
    <Wrapper onActiveChange={handleActiveChange} item={item}>
      <div className="flex flex-col items-center gap-[4px] cursor-pointer">
        {item.element && (
          <div
            className={cn(
              'rounded-2xl flex justify-center items-center h-8 min-w-12',
              isActive ? 'bg-[#12FFE2] text-[#021d3d]' : ''
            )}
          >
            {Element && <Element />}
          </div>
        )}
        {item.label && <p className="text-[10px] font-semibold leading-[14px]">{item.label}</p>}
      </div>
    </Wrapper>
  );
};

interface SidebarLinkProps {
  item: ItemType;
  children: ReactNode;
  onActiveChange?: (open: boolean) => void;
  className?: string;
}

const SidebarLink = ({ item, children, onActiveChange }: SidebarLinkProps) => {
  const { pathname, navigateWithQuery } = useQueryNavigation();

  useEffect(() => {
    if (onActiveChange && item.url) {
      if (pathname.includes(item.url)) {
        onActiveChange(true);
      } else {
        onActiveChange(false);
      }
    }
  }, [pathname]);

  const handleLinkClick = () => {
    item.url &&
      navigateWithQuery(
        item.url,
        item.url === '/sites' // Exclude params when navigating to '/sites'
          ? {
              excludeParams: [FILTER_PARAMS.TAB, FILTER_PARAMS.ASSETS_SECTION],
            }
          : undefined
      );
  };

  return (
    <div className="cursor-pointer" onClick={handleLinkClick}>
      {children}
    </div>
  );
};

interface SidebarAvatarProps {
  wrapper: WrapperType;
  item: ItemType;
  className?: string;
}

const SidebarButton = ({ wrapper, item }: SidebarAvatarProps) => {
  const Wrapper: any = wrapper;
  const Element = item.element;

  return (
    <Wrapper item={item}>
      {Element && <Element />}
      {item.label && <p className="text-[10px] leading-[14px] font-semibold">{item.label}</p>}
    </Wrapper>
  );
};

export { Sidebar, SidebarSection, SidebarItem, SidebarLink, SidebarButton };
